// ─── Phenotype-Specific Metric Panels ──────────────────────────────────────────
//
// Renders phenotype-specific metrics in compact (Dashboard) or full (Phenotype page) mode.
// Data extraction logic lives in lib/phenotypeMetrics.ts.
// ──────────────────────────────────────────────────────────────────────────────

import React from 'react';
import { PhenotypeResult } from '../lib/phenotype';
import { DailyMetrics, PhenotypeAppleExtras } from '../types';
import {
  MetricItem,
  getImmunometabolicMetrics,
  getHPAMetrics,
  getAnhedonicMetrics,
  getAnxiousMetrics,
  MECHANISM_TARGETS,
} from '../lib/phenotypeMetrics';

interface PhenotypeMetricPanelProps {
  phenotype: PhenotypeResult;
  metrics: DailyMetrics[];
  labData?: Record<string, { value: number; unit: string }>;
  appleExtras?: Pick<PhenotypeAppleExtras, 'restingHR' | 'weight'>;
  compact?: boolean;
  inline?: boolean;
  limitTo?: number;
}

function statusColor(status: MetricItem['status']): string {
  switch (status) {
    case 'good':    return '#22C55E';
    case 'warning': return '#F59E0B';
    case 'alert':   return '#EF4444';
    default:        return '#94A3B8';
  }
}

function statusBg(status: MetricItem['status']): string {
  switch (status) {
    case 'good':    return 'bg-green-50 dark:bg-green-900/20 border-green-100 dark:border-green-800/30';
    case 'warning': return 'bg-amber-50 dark:bg-amber-900/20 border-amber-100 dark:border-amber-800/30';
    case 'alert':   return 'bg-red-50 dark:bg-red-900/20 border-red-100 dark:border-red-800/30';
    default:        return 'bg-gray-50 dark:bg-gray-800/30 border-gray-100 dark:border-gray-700/30';
  }
}

function MiniSparkline({ data, color }: { data: number[]; color: string }) {
  if (data.length < 2) return null;
  const w = 48, h = 16, pad = 1;
  const min = Math.min(...data) - (Math.max(...data) - Math.min(...data)) * 0.15;
  const max = Math.max(...data) + (Math.max(...data) - Math.min(...data)) * 0.15;
  const rng = max - min || 1;
  const pts = data.map((v, i) => `${pad + (i / (data.length - 1)) * (w - pad * 2)},${h - pad - ((v - min) / rng) * (h - pad * 2)}`);
  return (
    <svg width={w} height={h} style={{ opacity: 0.8 }}>
      <polygon points={`${pts.join(' ')} ${w - pad},${h - pad} ${pad},${h - pad}`} fill={color} opacity={0.12} />
      <polyline points={pts.join(' ')} fill="none" stroke={color} strokeWidth={1.2} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function PhenotypeMetricPanel({
  phenotype,
  metrics,
  labData,
  appleExtras,
  compact = false,
  inline = false,
  limitTo,
}: PhenotypeMetricPanelProps) {
  let metricItems: MetricItem[] = [];
  switch (phenotype.id) {
    case 'immunometabolic':
      metricItems = getImmunometabolicMetrics(metrics, labData, appleExtras);
      break;
    case 'hpa_dominant':
      metricItems = getHPAMetrics(metrics, labData);
      break;
    case 'anhedonic':
      metricItems = getAnhedonicMetrics(metrics, labData);
      break;
    case 'anxious':
      metricItems = getAnxiousMetrics(metrics, appleExtras);
      break;
    default:
      console.warn(`[PhenotypeMetricPanel] Unknown phenotype: ${phenotype.id}`);
  }

  const treatment = MECHANISM_TARGETS[phenotype.id];

  if (limitTo !== undefined) metricItems = metricItems.slice(0, limitTo);

  if (metricItems.length === 0) return null;

  // ─── Compact mode for Dashboard ───────────────────────────────────────────
  if (compact) {
    const topMetrics = metricItems.slice(0, 4);
    return (
      <div className="card p-4">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: phenotype.color }} />
          <p className="text-xs font-semibold text-arete-navy">
            {phenotype.shortName} Focus Metrics
          </p>
          <span className="text-xs text-arete-muted ml-auto">7-day avg</span>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {topMetrics.map((item, i) => (
            <div key={i} className={`rounded-xl p-2.5 border ${statusBg(item.status)}`}>
              <div className="flex items-center justify-between">
                <p className="text-xs text-arete-muted dark:text-gray-400 truncate">{item.label}</p>
                {item.sparkline && <MiniSparkline data={item.sparkline} color={statusColor(item.status)} />}
              </div>
              <div className="flex items-baseline gap-1 mt-0.5">
                <span className="text-lg font-bold dark:text-gray-100" style={{ color: statusColor(item.status) }}>
                  {item.value}
                </span>
                {item.unit && <span className="text-xs text-arete-muted dark:text-gray-400">{item.unit}</span>}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ─── Full panel for Phenotype page ────────────────────────────────────────
  return (
    <div className={inline ? '' : 'card'}>
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: phenotype.color }} />
            <p className="text-sm font-bold text-arete-navy">
              Key Metrics
            </p>
          </div>
          <p className="text-xs text-arete-muted">
            Metrics most relevant to this pattern (7-day averages)
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
        {metricItems.map((item, i) => (
          <div key={i} className={`rounded-xl p-3 border ${statusBg(item.status)}`}>
            <div className="flex items-center justify-between">
              <p className="text-xs text-arete-muted dark:text-gray-400">{item.label}</p>
              {item.sparkline && <MiniSparkline data={item.sparkline} color={statusColor(item.status)} />}
            </div>
            <div className="flex items-baseline gap-1 mt-1">
              <span className="text-xl font-bold dark:text-gray-100" style={{ color: statusColor(item.status) }}>
                {item.value}
              </span>
              {item.unit && <span className="text-xs text-arete-muted dark:text-gray-400">{item.unit}</span>}
            </div>
            {item.detail && (
              <p className="text-xs text-arete-muted dark:text-gray-500 mt-1.5 leading-relaxed">{item.detail}</p>
            )}
          </div>
        ))}
      </div>

      {treatment && (
        <div className="bg-arete-light rounded-xl p-4">
          <p className="text-xs font-bold text-arete-navy mb-2">Underlying Systems Involved</p>
          <div className="space-y-1.5">
            {treatment.areas.map((area, i) => (
              <div key={i} className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0" style={{ backgroundColor: treatment.color }} />
                <p className="text-xs text-arete-muted">{area}</p>
              </div>
            ))}
          </div>
          <p className="text-xs text-arete-muted mt-3 italic">
            Underlying systems implicated in this pattern. Share with your provider to inform discussion — not clinical advice.
          </p>
        </div>
      )}
    </div>
  );
}
